import React, { useState, useEffect, useContext } from "react";
import { I18n, Auth } from "aws-amplify";
import { ConfigNavbar } from "@app/settings/configNavbar";
import { Layout, Divider, Switch } from "antd";
import { FlagOutlined, CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { DmImageSrc } from "@dm-portal/styleguide";
import { DmButton, DmCompanyPicker } from "dm-react-ui";
import { useLocation, useHistory } from "react-router-dom";
import ContentLoader from "react-content-loader";
import DmNavbarContext from "../../context/dmNavbarContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "@app/context/casl/abilityContext";
import { languages } from "@app/settings/languages";
import { settingsOptions } from "@app/settings/options";
import {
  getLoggedUser,
  getCustomerProducts,
  getUserSettings,
  updateUserSettings,
} from "@app/services/services.service";
import { UserMenu } from "./components/UserMenu/userMenu.component";
import { UserSettings } from "./components/UserSettings/userSettings.component";
import { AppToolMenu } from "./components/AppToolMenu/appToolMenu.component";
import getCustomerAccountsData from "./helpers/getCustomerAccountsData";
import getAppToolProducts from "./helpers/getAppToolProducts";
import getNormalizeData from "./helpers/getNormalizedData";
import "@app/assets/styles.css";

const { Header } = Layout;

export default function Navbar(props) {
  /**No need of the ability here at the moment */
  // const ability = useAbility(AbilityContext);
  const location = useLocation();
  const history = useHistory();
  const user = JSON.parse(localStorage.getItem("user-profile"));
  const [showNavbar, setShowNavbar] = useState(user ? true : false);
  const [userSettingsOptions, setUserSettingsOptions] = useState(
    settingsOptions
  );

  const [showSwitch, setShowSwitch] = useState(
    window.location.pathname === "/"
  );

  const [data, setData] = useState(null);
  const [apps, setApps] = useState(null);
  const [tools, setTools] = useState(null);
  const [optionPosition, setOptionPosition] = useState(null);
  const [optionType, setOptionType] = useState(null);
  const [locale, setLocale] = useState(
    localStorage.getItem("currentAppLanguage")
      ? localStorage.getItem("currentAppLanguage")
      : "es"
  );
  const [userPreferredLanguage, setUserPreferredLanguage] = useState();
  const [customerAccounts, setCustomerAccounts] = useState([]);
  const [currentAccount, setCurrentAccount] = useState();
  const [customerAccountsData, setCustomerAccountsData] = useState();
  /*Context */
  const [state, dispatch] = useContext(DmNavbarContext);
  const [isSandbox, setIsSandbox] = useState(localStorage.getItem('sandbox') === 'true');

  useEffect(() => {
    if (apps && tools) {
      updateAppTool();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, apps, tools]);

  useEffect(() => {
    Auth.currentSession()
      .then((session) => {})
      .catch(function (err) {
        localStorage.clear();
        window.location.reload(true);
      });
    Auth.currentAuthenticatedUser()
      .then((user) => {})
      .catch(function (err) {
        localStorage.clear();
        window.location.reload(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.addEventListener("single-spa:routing-event", (evt) => {
      checkShowNavbar();
    });
    return () => {
      window.removeEventListener("single-spa:routing-event", (evt) => {
        checkShowNavbar();
      });
    };
  }, []);

  useEffect(() => {
    getNavbarData();
    window.addEventListener("popstate", function () {
      setShowSwitch((prevState) => window.location.pathname === "/");
      getNavbarData();
    });

    return () => {
      window.removeEventListener("popstate", function () {
        getNavbarData();
      });
    };
  }, []);

  useEffect(() => {
    /*Detect user prefered language */
    getUserSettings({
      email: user.attributes.email,
    })
      .then((resp) => {
        if (!resp.data.getUserSettings.code === 200) {
          throw new Error(resp.data.getUserSettings.message);
        } else {
          if (resp.data.getUserSettings.data.language) {
            setUserPreferredLanguage(resp.data.getUserSettings.data.language);
            setLocale(resp.data.getUserSettings.data.language);
          } else {
            setUserPreferredLanguage("es");
            setLocale("es");
            updateUserSettings({
              email: user.attributes.email,
              language: "es",
            })
              .then((resp) => {
                if (!resp.data.updateUserSettings.code === 200) {
                  throw new Error(resp.data.updateUserSettings.message);
                } else {
                  console.log(
                    "new user settings data",
                    resp.data.updateUserSettings.message
                  );
                }
              })
              .catch((err) => console.log(err));
          }
        }
      })
      .catch((err) => console.log(err));
  }, []);

  useEffect(() => {
    /*Emit event to set language */
    window.dispatchEvent(
      new CustomEvent("i18n:changed", {
        detail: {
          language: locale,
          message: "The current Browser language",
        },
      })
    );
    I18n.setLanguage(locale);
    localStorage.setItem("currentAppLanguage", locale);
    if (userPreferredLanguage && locale !== userPreferredLanguage) {
      setUserPreferredLanguage(locale);
      updateUserSettings({
        email: user.attributes.email,
        language: locale,
      })
        .then((resp) => {
          if (!resp.data.updateUserSettings.code === 200) {
            throw new Error(resp.data.updateUserSettings.message);
          } else {
            console.log(
              "new user settings data",
              resp.data.updateUserSettings.message
            );
          }
        })
        .catch((err) => console.log(err));
    }
  }, [locale]);

  useEffect(() => {
    /*Emit event to set current customer code */
    if (currentAccount) {
      window.dispatchEvent(
        new CustomEvent("currentCustomerAccountData:changed", {
          detail: {
            customerData: customerAccountsData[currentAccount],
            message: "The current customer account data",
          },
        })
      );
      dispatch({
        type: "UPDATE_CUSTOMER_CODE",
        payload: customerAccountsData[currentAccount].accountCustomerCode,
      });
      localStorage.setItem(
        "currentCustomerAccountData",
        JSON.stringify(customerAccountsData[currentAccount])
      );
      localStorage.setItem("currentAccountId", currentAccount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentAccount]);

  useEffect(() => {
    /*future api call to determinate user customer codes */
    getLoggedUser({
      customer_code: Object.keys(user.userPermissions)[0],
    }).then((resp) => {
      if (!resp.data.getLoggedUser.code === 200) {
        throw new Error(resp.data.getLoggedUser.message);
      } else {
        setCustomerAccountsData(
          getCustomerAccountsData(resp.data.getLoggedUser.data.accounts)
        );
        setCurrentAccount(
          localStorage.getItem("currentAccountId") ||
            resp.data.getLoggedUser.data.accounts[0].account_id
        );
        setCustomerAccounts(
          getNormalizeData(resp.data.getLoggedUser.data.accounts)
        );
        // }
      }
    });
  }, []);

  useEffect(() => {
    /*api call to determinate available products and tools */
    if (currentAccount) {
      getCustomerProducts({
        customer_code: customerAccountsData[currentAccount].accountCustomerCode,
      }).then((resp) => {
        if (!resp.data.getCustomerProducts.code === 200) {
          throw new Error(resp.data.getCustomerProducts.message);
        } else {
          const currentProducts = getAppToolProducts(
            resp.data.getCustomerProducts.data.products
          );
          setApps(currentProducts["app"]);
          setTools(currentProducts["tool"]);
        }
      });
    }
  }, [currentAccount]);

  const checkShowNavbar = () => {
    let app = window.location.pathname.split("/")[1] || "portal";
    /** temporal */
    app = app === "poc" || app === "portal" ? "portal" : app;
    setShowNavbar(user && ConfigNavbar[app] ? true : false);
  };

  const updateAppTool = () => {
    let currentApps = apps;
    currentApps.forEach((app) => {
      if (window.location.pathname.includes(app.path)) {
        app.active = true;
        setOptionPosition(currentApps.indexOf(app));
        setOptionType(app.type);
      } else {
        app.active = false;
      }
    });
    let currentTools = tools;
    currentTools.forEach((tool) => {
      if (window.location.pathname.includes(tool.path)) {
        tool.active = true;
        setOptionPosition(currentTools.indexOf(tool));
        setOptionType(tool.type);
      } else {
        tool.active = false;
      }
    });
  };

  const getNavbarData = () => {
    setTimeout(() => {
      let app = window.location.pathname.split("/")[1] || "portal";
      /** temporal */
      app = app === "poc" || app === "portal" ? "portal" : app;
      app = Object.keys(ConfigNavbar).includes(app) ? app : "getdata";
      setData(ConfigNavbar[app]);
    }, 1000);
  };

  const singOut = () => {
    localStorage.clear();
    window.location.reload(true);
  };

  const handleLocaleChange = (value) => {
    value === "English" ? I18n.setLanguage("en") : I18n.setLanguage("es");    
    setLocale(value === "English" ? "en" : "es");
  };

  const handleAccountChange = (value) => {
    setCurrentAccount(value);
  };

  const activateAppTool = (options, value) => {
    const currentOptionPosition = options.indexOf(value);
    let currentOptions = options;
    if (optionPosition !== null && value.type === optionType) {
      currentOptions[optionPosition].active = false;
    }
    currentOptions[currentOptionPosition].active = true;
    setOptionPosition(currentOptionPosition);
    setOptionType(value.type);
    return currentOptions;
  };

  const handleSwitchApp = (value) => {
    if (optionType && optionType === "tool") {
      let currentTools = tools;
      currentTools[optionPosition].active = false;
      setOptionPosition(null);
      setTools(currentTools);
    }
    const currentApps = activateAppTool(apps, value);
    setApps(currentApps);
    history.push(value.path);
  };

  const handleSwitchTool = (value) => {
    if (optionType && optionType === "app") {
      let currentApps = apps;
      currentApps[optionPosition].active = false;
      setOptionPosition(null);
      setApps(currentApps);
    }
    const currentTools = activateAppTool(tools, value);
    setTools(currentTools);
    history.push(value.path);
  };

  const onChangeSandbox = (checked) => {
    setIsSandbox(checked);
    localStorage.setItem('sandbox', checked);
    setTimeout(() => {
      window.location.reload();
    }, 300);
  };

  return (
    showNavbar && (
        <Header className="header" style={{ zIndex: "1" }}>
          <AppToolMenu
            apps={apps?.filter(app => app.code !== 'rules') || []}
            tools={tools || []}
            locale={locale}
            appHeaderText={I18n.get("Cambiar de app")}
            toolHeaderText={I18n.get("Otras herramientas")}
            onClickApp={(value) => handleSwitchApp(value)}
            onClickTool={(value) => handleSwitchTool(value)}
          />
          <div
            onClick={() =>
              data !== null && data.pathHome ? history.push(data.pathHome) : {}
            }
            role="link"
            style={{
              flexBasis: 183
            }}
            tabIndex={0}
          >
            <img
              className="img"
              src={data !== null ? DmImageSrc(data.logoText || "getdata") : null}
              alt=""
            />
          </div>

          {data && data.pathHome != "/" && (
            <Divider
              type="vertical"
              style={{
                height: "100%",
                marginRight: "20px"              
              }}
            />
          )}
          {data && data.subscriptions && (
            <DmButton
              onClick={() => history.push(`/getdata/subscriptions`)}
              type="primary"
              style={{ width: "300px !important" }}
            >
              {I18n.get("Suscripciones")}
            </DmButton>
          )}
          <div className="fullfill"></div>
          {process.env.STAGE === 'prod' && location.pathname.includes('/lendbot') && (
            <div style={{ display: "flex", marginRight: "20px" }}>
                <Switch 
                  className="custom-switch"
                  checked={isSandbox}
                  onChange={onChangeSandbox} 
                  checkedChildren={<span>Sandbox <CheckOutlined /></span>}
                  unCheckedChildren={<span>Sandbox <CloseOutlined /></span>} 
                  />
            </div>
          )}
          {customerAccounts.length > 0 ? (
            <DmCompanyPicker
              // disabled={customerCompanies.length > 1}
              // showArrow={customerCompanies.length > 1}
              style={{ display: "flex", alignItems: "center" }}
              value={currentAccount || "undefined"}
              onSelect={(value) => handleAccountChange(value)}
              placeholder={I18n.get("Seleccione una cuenta")}
              onChange={() => {}}
              accounts={customerAccounts.filter((item, index, self) => {
                return index === self.findIndex((t) => t.accountId === item.accountId);
              })} //Validate duplicates
            />
          ) : (
            <div style={{ display: "flex", alignItems: "center" }}>
              <ContentLoader
                speed={2}
                width={250}
                height={40}
                style={{ borderRadius: "6px" }}
                viewBox="0 0 250 40"
                backgroundcolor="#f3f3f3"
                foregroundcolor="#ecebeb"
              >
                <rect x="0" y="0" rx="3" ry="3" width="250" height="40" />
              </ContentLoader>
            </div>
          )}
          <div style={{ display: "flex", marginLeft: "20px" }}>
            <UserSettings
              headerText={I18n.get("Administración")}
              options={userSettingsOptions}
              onClick={(path) => history.push(`${path}`)}
              languageText={I18n.get("Idioma")}
              languages={languages}
              locale={locale === "en" ? "English" : "Español"}
              handleLocaleChange={handleLocaleChange}
            />
          </div>
          <div className="user1">
            <UserMenu
              name={user.attributes.given_name}
              surname={user.attributes.family_name}
              email={user.attributes.email}
              options={[]}
              logoutActionText={I18n.get("Cerrar sesión")}
              onClickUserAction={() => history.push("/profile")}
              onLogout={() => singOut()}
            />
          </div>
        </Header>
      )
  );
}
